import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AuthService } from '@rekruto/core';

const COMPANY_ID_STORAGE_KEY = 'rekruto_company_id';
const COMPANY_ID_CLAIM = 'company_id';

/** The company roles (docs/11 role matrix). `admin` is the platform role, never a seat. */
export type CompanyRole = 'owner' | 'recruiter' | 'hiring_manager' | 'viewer' | 'admin';

const DECIDING_ROLES: readonly CompanyRole[] = ['owner', 'recruiter', 'hiring_manager'];

/**
 * The tenant context every company-scoped read and write needs: the company id the API's query
 * parameters and command bodies carry. The API has no "my company" discovery endpoint, so the id
 * arrives from the access token's `company_id` claim when the API issues one, and from localStorage
 * where onboarding (whose response carries the id) and earlier sessions leave it.
 *
 * `companyId` is a COMPUTED, not a stateful signal fed by an effect: a route guard evaluates it
 * synchronously on the navigation that follows a login, and an effect would not have run yet, so a
 * freshly logged-in owner would be bounced to onboarding. The computed reads the token signal
 * directly, so it is correct in the very tick the token is adopted. A small effect only persists
 * the resolved id for the next boot.
 */
@Injectable({ providedIn: 'root' })
export class CompanyContextService {
  // Field-initializer order matters: the auth service (and its token signal) must exist before the
  // computed below reads the company_id claim off the access token.
  readonly #auth = inject(AuthService);

  /** An id adopted from an onboarding response, before a token carries it. */
  readonly #adopted = signal<string | null>(null);

  /** The tenant id, or null until it is known; scoped reads stay unloaded while it is null. */
  readonly companyId = computed<string | null>(
    () => this.#adopted() ?? this.#fromToken() ?? this.#fromStorage(),
  );

  /** The seat's decision rights: viewer (and non-seat roles) cannot record or override (docs/11). */
  readonly canDecide = computed(() =>
    DECIDING_ROLES.includes(this.#auth.role() as CompanyRole),
  );

  readonly role = this.#auth.role;
  readonly email = this.#auth.email;

  constructor() {
    // Persist the resolved id for the next session. The computed above is the synchronous source
    // of truth; this write is only for a cold boot where the token (and claim) is not present yet.
    effect(() => {
      const id = this.companyId();
      if (!id) return;
      try {
        localStorage.setItem(COMPANY_ID_STORAGE_KEY, id);
      } catch {
        // A blocked storage must not break the session; the computed already carries the id.
      }
    });
  }

  /** Adopts a known company id (e.g. from an onboarding response) for this session. */
  adopt(companyId: string): void {
    if (!companyId) return;
    // Same value: leave both the signal and storage untouched (a no-op re-adoption must not rewrite).
    if (this.#adopted() === companyId || this.#fromStorage() === companyId) return;
    this.#adopted.set(companyId);
    try {
      localStorage.setItem(COMPANY_ID_STORAGE_KEY, companyId);
    } catch {
      // A blocked storage must not break the session; the computed already carries the id.
    }
  }

  #fromToken(): string | null {
    const token = this.#auth.token();
    if (!token) return null;
    const payload = token.split('.')[1];
    if (!payload) return null;
    try {
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
      const claim = json[COMPANY_ID_CLAIM];
      return typeof claim === 'string' && claim.length > 0 ? claim : null;
    } catch {
      return null;
    }
  }

  #fromStorage(): string | null {
    try {
      const value = localStorage.getItem(COMPANY_ID_STORAGE_KEY);
      return value && value.length > 0 ? value : null;
    } catch {
      return null;
    }
  }
}
